<template>
  <div class="container-fluid">
    <div class="row mb-5">
      <span class="display-4 col-12">{{ product.name }}</span>
    </div>

    <div class="row">
      <div class="col-9">
        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.NAME") }}</dt>
          <dd class="col-sm-8">
            {{ product.name }}
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.CATEGORIES") }}</dt>
          <dd class="col-sm-8">
            <category
              v-for="(category, key) in product.categories"
              :key="key"
              :category="category"
            />
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.STATUS") }}</dt>
          <dd class="col-sm-8">
            <div class="d-flex">
              <product-status-badge :product="product" />
              <product-novelty-badge :product="product" />
            </div>
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("PRODUCTS.SPECIAL_ORDER") }}</dt>
          <dd class="col-sm-8">
            <icon-check :checked="product.special_order" />
          </dd>
        </dl>
        <dl class="row" v-if="product.status == STATUS_BACKORDER">
          <dt class="col-sm-4">{{ $t("PRODUCTS.RETURN_TO_STOCK_DATE") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(product.return_to_stock_date) }}
          </dd>
        </dl>
        <dl class="row" v-if="product.status == STATUS_BACKORDER">
          <dt class="col-sm-4">{{ $t("PRODUCTS.REPLACEMENTS") }}</dt>
          <dd class="col-sm-8">
            <div v-for="prod of product.replacements" :key="prod.id">
              <object-link :object="prod" /><br />
            </div>
          </dd>
        </dl>
        <dl class="row" v-if="product.status == STATUS_BACKORDER && product.backorder_note">
          <dt class="col-sm-4">{{ $t("PRODUCTS.BACKORDER_NOTE") }}</dt>
          <dd class="col-sm-8">
            {{ product.backorder_note }}
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("PRODUCTS.SKU") }}</dt>
          <dd class="col-sm-8">
            {{ product.sku }}
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("PRODUCTS.BARCODE") }}</dt>
          <dd class="col-sm-8">
            {{ product.barcode || "-" }}
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("PRODUCTS.POS_DEPARTMENT_CODE") }}</dt>
          <dd class="col-sm-8">
            {{ product.pos_department_code || "-" }}
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("PRODUCTS.POS_DEPARTMENT_NAME") }}</dt>
          <dd class="col-sm-8">
            {{ product.pos_department_name || "-" }}
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.UNIT_OF_MEASURE") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="product.unitOfMeasure" />
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("PRODUCTS.CAN_BE_SOLD") }}</dt>
          <dd class="col-sm-8">
            <icon-check :checked="product.can_be_sold" />
          </dd>
        </dl>
        <dl class="row" v-if="product.can_be_sold">
          <dt class="col-sm-4">{{ $t("COMMON.SELLING_PRICE") }}</dt>
          <dd class="col-sm-8">
            {{ $formatCurrency(product.selling_price) }}
          </dd>
        </dl>
        <dl class="row" v-if="product.can_be_sold">
          <dt class="col-sm-4">{{ $t("PRODUCTS.HAS_DEPOSIT") }}</dt>
          <dd class="col-sm-8">
            <icon-check :checked="product.has_deposit" />
          </dd>
        </dl>
        <dl class="row" v-if="product.has_deposit">
          <dt class="col-sm-4">{{ $t("PRODUCTS.DEPOSIT_AMOUNT") }}</dt>
          <dd class="col-sm-8">
            {{ $formatCurrency(product.deposit_amount) }}
          </dd>
        </dl>
        <dl class="row" v-if="product.has_deposit && product.can_be_purchased">
          <dt class="col-sm-4">{{ $t("PRODUCTS.BUYING_DEPOSIT_AMOUNT") }}</dt>
          <dd class="col-sm-8">
            {{ $formatCurrency(product.buying_deposit_amount) }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("PRODUCTS.CAN_BE_PURCHASED") }}</dt>
          <dd class="col-sm-8">
            <icon-check :checked="product.can_be_purchased" />
          </dd>
        </dl>
        <dl class="row" v-if="product.can_be_purchased">
          <dt class="col-sm-4">{{ $t("COMMON.BUYING_PRICE") }}</dt>
          <dd class="col-sm-8">
            {{ $formatCurrency(product.buying_price) }}
          </dd>
        </dl>

        <dl class="row" v-if="product.can_be_purchased">
          <dt class="col-sm-4">
            {{ $t("PRODUCTS.ORDER_UNIT_OF_MEASURE_UNIT_PRICE") }}
          </dt>
          <dd class="col-sm-8">
            {{ $formatCurrency(product.orderUnitOfMeasureUnitPrice) }}
          </dd>
        </dl>

        <dl
          class="row"
          v-if="product.can_be_purchased && product.orderUnitOfMeasureUnit"
        >
          <dt class="col-sm-4">
            {{ $t("PRODUCTS.ORDER_UNIT_OF_MEASURE_UNIT") }}
          </dt>
          <dd class="col-sm-8">
            <object-link :object="product.orderUnitOfMeasureUnit" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.DEFAULT_TAX_GROUPS") }}</dt>
          <dd class="col-sm-8">
            <tax-groups :taxGroups="product.taxGroups" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.EXCERPT") }}</dt>
          <dd class="col-sm-8">
            <div v-html="product.excerpt"></div>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.LOCATIONS") }}</dt>
          <dd class="col-sm-8">
            <locations :locations="product.allowedLocations" />
          </dd>
        </dl>
        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
        >
          <dt class="col-sm-4">{{ $t("COMMON.ORGANIZATION") }}</dt>
          <dd class="col-sm-8">
            <organization :organization="product.organization" />
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("PRODUCTS.ORIGIN") }}</dt>
          <dd class="col-sm-8">
            {{
              product.origin
                ? $t(`PRODUCTS.PRODUCT_ORIGIN_${product.origin}`)
                : null
            }}
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.CREATED_AT") }}</dt>
          <dd class="col-sm-8" v-if="product.created_at">
            {{ $timeZoneDateFormat(product.created_at) }}
          </dd>
        </dl>
        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.UPDATED_AT") }}</dt>
          <dd class="col-sm-8" v-if="product.updated_at">
            {{ $timeZoneDateFormat(product.updated_at) }}
          </dd>
        </dl>
      </div>
      <div class="col-3">
        <img
          v-if="product.picture"
          :src="`${product.picture}`"
          class="argon-image mb-2"
          style="width: 100%"
        />
        <img v-else src="/img/placeholder.jpg" class="argon-image mb-2" />
      </div>
    </div>
  </div>
</template>

<script>
import { STATUS_BACKORDER } from "@/constants/products";
import ProductStatusBadge from "@/components/ProductStatusBadge";
import ProductNoveltyBadge from "@/components/ProductNoveltyBadge";
import Category from "@/components/CategoryLabel";
import TaxGroups from "@/components/TaxGroups.vue";
import IconCheck from "@/components/IconCheck.vue";

export default {
  name: "product-view-global",

  components: {
    Category,
    ProductStatusBadge,
    ProductNoveltyBadge,
    TaxGroups,
    IconCheck,
  },

  props: ["product"],

  data() {
    return {
      STATUS_BACKORDER,
    };
  },

  computed: {},

  created() {},

  methods: {},

  mounted() {},

  watch: {
    product(product) {},
  },
};
</script>
